// Localização: frontend/src/components/Financeiro/DashboardGridLayout.jsx
// Componente de Grid Layout com Drag and Drop para Dashboard Financeiro

import React, { useState } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import './DashboardGridLayout.css';
import { GripVertical } from 'lucide-react';

const ResponsiveGridLayout = WidthProvider(Responsive);

/**
 * Componente de Grid Layout responsivo com drag-and-drop
 * @param {Object} props
 * @param {Array} props.layout - Layout dos widgets
 * @param {Function} props.onLayoutChange - Callback quando o layout muda
 * @param {boolean} props.isEditMode - Se está em modo de edição
 * @param {Object} props.widgets - Objeto com os widgets a serem renderizados { id: ReactNode }
 * @param {Object} props.breakpoints - Breakpoints responsivos (opcional)
 * @param {Object} props.cols - Colunas por breakpoint (opcional)
 */
const DashboardGridLayout = ({
    layout,
    layoutMobile,
    onLayoutChange,
    isEditMode = false,
    widgets = {},
    breakpoints = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 },
    cols = { lg: 4, md: 4, sm: 4, xs: 4, xxs: 2 },
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [draggingItem, setDraggingItem] = useState(null);

    // Converter layout para formato do react-grid-layout
    // Importar layout mobile do hook (será passado via prop)
    const layouts = {
        lg: layout,
        md: layout,
        sm: layout,
        xs: layout,
        xxs: layoutMobile || layout, // Usa layout mobile se fornecido, senão usa o layout padrão
    };

    // Handler para quando começa o drag
    const handleDragStart = (layout, oldItem, newItem, placeholder, e, element) => {
        setIsDragging(true);
        setDraggingItem(newItem.i);
    };

    // Handler para quando termina o drag
    const handleDragStop = (layout, oldItem, newItem, placeholder, e, element) => {
        setIsDragging(false);
        setDraggingItem(null);
    };

    // Handler para mudanças de layout
    const handleLayoutChange = (currentLayout, allLayouts) => {
        // O react-grid-layout passa o layout atual e todos os layouts por breakpoint
        // Usamos o layout do breakpoint 'lg' como padrão para persistência
        if (allLayouts && allLayouts.lg) {
            onLayoutChange(allLayouts.lg, allLayouts);
        } else if (Array.isArray(currentLayout)) {
            onLayoutChange(currentLayout, allLayouts);
        }
    };

    return (
        <div className="dashboard-grid-layout">
            <ResponsiveGridLayout
                className="layout"
                layouts={layouts}
                breakpoints={breakpoints}
                cols={cols}
                rowHeight={60}
                isDraggable={isEditMode}
                isResizable={isEditMode}
                margin={[16, 16]}
                containerPadding={[0, 0]}
                onLayoutChange={handleLayoutChange}
                onDragStart={handleDragStart}
                onDragStop={handleDragStop}
                // No modo de edição, permite arrastar de qualquer lugar
                draggableHandle={isEditMode ? undefined : ".drag-handle"}
                // Permitir sobreposição durante o drag para criar espaço visual
                preventCollision={false}
                // Compactar verticalmente após soltar
                compactType="vertical"
                // Permitir redimensionamento apenas no modo de edição
                resizeHandles={isEditMode ? ['se', 'sw', 'ne', 'nw', 'e', 'w', 's', 'n'] : []}
                // Permitir sobreposição temporária durante o drag
                allowOverlap={isDragging}
                // Forçar snap ao grid para manter estrutura rígida de 1/4
                snapToGrid={true}
            >
                {Object.entries(widgets).map(([id, widget]) => (
                    <div 
                        key={id} 
                        className={`widget-container ${isDragging && draggingItem !== id ? 'widget-push-away' : ''}`}
                    >
                        {isEditMode && (
                            <div className="drag-handle absolute top-2 right-2 z-10 cursor-move p-1 bg-white rounded shadow-sm hover:bg-gray-100 transition-colors">
                                <GripVertical className="w-4 h-4 text-gray-500" />
                            </div>
                        )}
                        <div className="h-full w-full">
                            {widget}
                        </div>
                    </div>
                ))}
            </ResponsiveGridLayout>
        </div>
    );
};

export default DashboardGridLayout;

